import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import Logo from './Logo';

const StyledHeader = styled.header`
  background-color: var(--color-white);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  position: fixed;
  width: 100%;
  top: 0;
  z-index: 1000;
  transition: all 0.3s ease;
  
  &.scrolled {
    padding: 0.5rem 0;
  }
`;

const HeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Nav = styled.nav`
  @media (max-width: 768px) {
    display: ${props => (props.isOpen ? 'flex' : 'none')};
    flex-direction: column;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background-color: var(--color-white);
    box-shadow: 0 5px 10px rgba(0, 0, 0, 0.1);
    padding: 1rem 0;
  }
  
  @media (min-width: 769px) {
    display: flex;
  }
`;

const NavLink = styled(Link)`
  color: var(--color-primary);
  margin: 0 1rem;
  font-weight: 600;
  padding: 0.5rem;
  position: relative;
  font-family: 'Montserrat', sans-serif;
  
  &:after {
    content: '';
    position: absolute;
    width: 0;
    height: 3px;
    bottom: 0;
    left: 0;
    background-color: var(--color-accent);
    transition: width 0.3s ease;
  }
  
  &:hover:after,
  &.active:after {
    width: 100%;
  }
  
  @media (max-width: 768px) {
    margin: 0.5rem 2rem;
  }
`;

const SSOButton = styled.a`
  background-color: var(--color-accent);
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-weight: 600;
  transition: all 0.3s ease;
  margin-left: 1rem;
  font-family: 'Montserrat', sans-serif;
  
  &:hover {
    background-color: var(--color-primary-dark);
    color: white;
  }
  
  @media (max-width: 768px) {
    margin: 1rem 2rem;
    text-align: center;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.5rem;
  color: var(--color-primary);
  
  @media (max-width: 768px) {
    display: block;
  }
`;

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return (
    <StyledHeader className={isScrolled ? 'scrolled' : ''}>
      <HeaderContainer>
        <Logo />
        
        <MobileMenuButton onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}>
          {isMobileMenuOpen ? '✕' : '☰'}
        </MobileMenuButton>
        
        <Nav isOpen={isMobileMenuOpen}>
          <NavLink 
            to="/" 
            className={location.pathname === '/' ? 'active' : ''}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Home
          </NavLink>
          <NavLink 
            to="/about" 
            className={location.pathname === '/about' ? 'active' : ''}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            About
          </NavLink>
          <NavLink 
            to="/services" 
            className={location.pathname === '/services' ? 'active' : ''}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Services
          </NavLink>
          <NavLink 
            to="/projects" 
            className={location.pathname === '/projects' ? 'active' : ''}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Projects
          </NavLink>
          <NavLink 
            to="/tech-stack" 
            className={location.pathname === '/tech-stack' ? 'active' : ''}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Tech Stack
          </NavLink>
          <NavLink 
            to="/contact" 
            className={location.pathname === '/contact' ? 'active' : ''}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            Contact
          </NavLink>
          
          <SSOButton 
            href="https://dev-84918634.okta.com/app/zendesk/exkt89ftiX6QbfOxz5d6/sso/saml"
            target="_blank"
            rel="noopener noreferrer"
          >
            SSO Sign In
          </SSOButton>
        </Nav>
      </HeaderContainer>
    </StyledHeader>
  );
};

export default Header;